import React from "react";
import { createRoot } from "react-dom/client";
import { Activity, ArrowLeft, BarChart3, CalendarDays, CheckCircle2, ChevronDown, ChevronRight, ClipboardCheck, Download, Dumbbell, Gauge, Globe2, HeartPulse, History, Info, Lightbulb, LogOut, MessageSquareText, Minus, Pause, Pencil, Play, Plus, RotateCcw, Save, Scale, Search, Target, Timer, Trash2, TrendingUp, TriangleAlert, UserRound, WifiOff, X } from "lucide-react";
import { SORENESS_LEVELS } from "../shared/recovery";
import {
  adjustRestTimer,
  createRestTimer,
  findPreviousExercise,
  getSetPrefill,
  parseRestTimer,
  pauseRestTimer,
  reconcileRestTimer,
  resetRestTimer,
  resumeRestTimer
} from "../shared/training";
import type { PreviousExerciseRecord, RestTimerState } from "../shared/training";
import { buildSetPayload, getTrackingFields } from "../shared/tracking";
import { isPrimaryViewActive, primaryViewFor, type AppView } from "../shared/navigation";
import type { BodyMetric, Exercise, Language, NextSessionRecommendation, RecoveryWindow, SessionOverallEvaluation, SorenessLevel, TrainingPlan, TrainingPlanExercise, TrainingSession, WeeklyVolume } from "../shared/types";
import { getLocalDateString } from "../shared/volume";
import { completedExercises, completedSetCount } from "../shared/session";
import { APP_VERSION, activateServiceWorkerUpdate, registerServiceWorker } from "./pwa";
import "./styles.css";

type View = AppView;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const messages = {
  "zh-CN": {
    appTitle: "训练记录",
    password: "访问密码",
    login: "登录",
    logout: "退出",
    today: "今日训练",
    volume: "容量",
    history: "历史",
    body: "体重",
    recovery: "恢复",
    trainNav: "训练",
    insightsNav: "洞察",
    profileNav: "我的",
    insightsTitle: "训练洞察",
    profileTitle: "我的",
    currentWeight: "当前体重",
    weightChange: "较上次",
    account: "账户",
    signedIn: "已登录",
    appSection: "应用",
    appVersion: "应用版本",
    installApp: "安装到主屏幕",
    downloadAndroid: "下载 Android 安装包",
    installUnavailable: "可在浏览器菜单中添加到主屏幕",
    offline: "当前离线：记录不会显示为保存成功",
    exerciseSearch: "搜索动作名称",
    chooseExercises: "选择动作",
    confirmAdd: "确认添加",
    addSelected: "添加已选",
    alreadyAdded: "已添加",
    clearSelection: "清空已选",
    selectedOrder: "按勾选顺序添加",
    addingExercises: "正在添加…",
    noMatchingExercises: "没有找到匹配的动作",
    removeSelection: "取消选择",
    prefillCurrent: "已沿用本次上一组",
    prefillPrevious: "已参考上次训练",
    exerciseProgress: "动作进度",
    elapsed: "已训练",
    closeDetails: "关闭说明",
    finishConfirm: "确认结束本次训练？结束后仍可在历史中查看记录。",
    updateReady: "新版本已准备好",
    updateNow: "立即更新",
    recoveryTitle: "次日部位反馈",
    recoveryExercises: "昨日动作",
    recoveryNotes: "恢复备注",
    recoveryPending: "待填写",
    recoveryRecorded: "已记录",
    saveRecovery: "保存反馈",
    soreness0: "没什么感觉",
    soreness1: "有点感觉",
    soreness2: "酸痛",
    soreness3: "十分酸痛",
    soreness4: "根本不想动",
    coachMode: "教练模式",
    coachPending: "待设计",
    coachReady: "可开始回顾",
    coachInsufficient: "数据积累中",
    overallEvaluation: "整体评估",
    overallPending: "等待后台整体复盘",
    evaluationProvisional: "暂定评估",
    evaluationFinal: "已定稿",
    overallProgress: "进步信号",
    overallAttention: "需要关注",
    overallCapacity: "一小时容量",
    overallNextFocus: "下次训练重点",
    evaluatedAt: "更新时间",
    coachRecent: "近期训练",
    coachQuestions: "之后可分析",
    recommendationTitle: "下次训练建议",
    back: "返回",
    recommendationSummary: "查看训练观察与下一次安排",
    exerciseSwitcher: "切换训练动作",
    previousExercise: "上一个动作",
    nextExercise: "下一个动作",
    fullRange: "扩大范围",
    nearbyRange: "常用范围",
    notSet: "未设置",
    completedSets: "已完成组",
    effortLevel: "用力程度",
    remainingReps: "剩余次数",
    trainingReference: "上次表现与备注",
    recommendationObservations: "观察",
    recommendationSuggestions: "建议",
    planTitle: "教练训练安排",
    planBadge: "教练已安排",
    planEdit: "调整安排",
    planDoneEditing: "完成调整",
    planSave: "保存计划",
    planAddExercise: "添加计划动作",
    planRemoveExercise: "移除动作",
    planStart: "按安排开始训练",
    planCoachNotes: "教练说明",
    planStartTime: "计划时间",
    planTargetSets: "目标组数",
    planTargetRepsMin: "最低次数",
    planTargetRepsMax: "最高次数",
    planTargetWeight: "目标重量 kg",
    planTargetDuration: "目标时间 分钟",
    planTargetDistance: "目标距离 m",
    planTargetRpe: "目标 RPE",
    planTargetRir: "目标 RIR",
    planRestSeconds: "休息 秒",
    planExerciseNotes: "动作提示",
    planEmpty: "安排里还没有动作。",
    planSaveExercise: "保存动作",
    startSession: "开始训练",
    finishSession: "结束训练",
    todayCompleted: "今日训练已结束",
    trainingDetail: "训练详情",
    backToHistory: "返回历史",
    viewDetail: "查看详情",
    ended: "已结束",
    inProgress: "进行中",
    exerciseCount: "个动作",
    setCount: "组",
    noSets: "还没有组记录",
    fatigue: "疲劳",
    pain: "疼痛",
    notes: "备注",
    sessionNotes: "本次训练备注",
    exerciseNotes: "本动作备注",
    notePlaceholder: "记录感受、问题或下次需要关注的事项",
    saveNote: "保存备注",
    nextExerciseAdvice: "下次动作建议",
    evaluationPending: "等待后台复盘",
    addExercise: "加入动作",
    chooseExercise: "选择动作",
    muscleFilter: "部位筛选",
    allBodyParts: "全部部位",
    addSet: "记录一组",
    copyLastSet: "复制本次上一组",
    previousWorkout: "上次训练",
    noPreviousWorkout: "暂无上次记录",
    restTimer: "组间休息",
    startRest: "开始休息",
    pauseRest: "暂停",
    resumeRest: "继续",
    resetRest: "重新计时",
    closeRest: "关闭计时器",
    restFinished: "休息结束",
    subtract15: "减少 15 秒",
    add15: "增加 15 秒",
    weightKg: "重量 kg",
    reps: "次数",
    durationMin: "时间 分钟",
    distanceM: "距离 m",
    rpe: "RPE",
    rir: "RIR",
    rpeHint: "主观用力 1-10",
    rirHint: "还能做几次",
    noSession: "今天还没有开始训练。",
    noHistory: "还没有训练记录。",
    noMetrics: "还没有体重记录。",
    week: "本周",
    low: "不足",
    ok: "目标内",
    high: "偏高",
    bodyWeight: "体重 kg",
    save: "保存",
    description: "动作说明",
    setup: "准备姿势",
    execution: "执行步骤",
    cues: "要点",
    cautions: "注意",
    commonMistakes: "常见错误",
    image: "动作图片",
    oneHourHint: "每日训练窗口：最多 1 小时",
    language: "语言",
    error: "请求失败",
    loading: "加载中"
  },
  "ja-JP": {
    appTitle: "トレーニング記録",
    password: "アクセスパスワード",
    login: "ログイン",
    logout: "ログアウト",
    today: "今日のトレーニング",
    volume: "ボリューム",
    history: "履歴",
    body: "体重",
    recovery: "回復",
    trainNav: "トレーニング",
    insightsNav: "インサイト",
    profileNav: "マイページ",
    insightsTitle: "トレーニングインサイト",
    profileTitle: "マイページ",
    currentWeight: "現在の体重",
    weightChange: "前回比",
    account: "アカウント",
    signedIn: "ログイン中",
    appSection: "アプリ",
    appVersion: "アプリバージョン",
    installApp: "ホーム画面に追加",
    downloadAndroid: "Android アプリをダウンロード",
    installUnavailable: "ブラウザのメニューからホーム画面に追加できます",
    offline: "オフラインです。保存成功として表示しません",
    exerciseSearch: "種目名を検索",
    chooseExercises: "種目を選択",
    confirmAdd: "選択した種目を追加",
    addSelected: "選択した種目を追加",
    alreadyAdded: "追加済み",
    clearSelection: "選択をクリア",
    selectedOrder: "選択順で追加",
    addingExercises: "追加中…",
    noMatchingExercises: "一致する種目がありません",
    removeSelection: "選択を解除",
    prefillCurrent: "今回の前セットから入力済み",
    prefillPrevious: "前回の記録を参考入力",
    exerciseProgress: "種目の進捗",
    elapsed: "経過時間",
    closeDetails: "説明を閉じる",
    finishConfirm: "今回のトレーニングを終了しますか？記録は履歴から確認できます。",
    updateReady: "新しいバージョンを利用できます",
    updateNow: "今すぐ更新",
    recoveryTitle: "翌日の部位フィードバック",
    recoveryExercises: "昨日の種目",
    recoveryNotes: "回復メモ",
    recoveryPending: "未入力",
    recoveryRecorded: "記録済み",
    saveRecovery: "フィードバックを保存",
    soreness0: "ほとんど感じない",
    soreness1: "少し感じる",
    soreness2: "筋肉痛",
    soreness3: "かなり痛い",
    soreness4: "動きたくない",
    coachMode: "コーチモード",
    coachPending: "設計中",
    coachReady: "レビュー可能",
    coachInsufficient: "データ蓄積中",
    overallEvaluation: "総合評価",
    overallPending: "バックエンドの総合レビュー待ち",
    evaluationProvisional: "暫定評価",
    evaluationFinal: "確定",
    overallProgress: "進歩のサイン",
    overallAttention: "注意点",
    overallCapacity: "1時間の容量",
    overallNextFocus: "次回の重点",
    evaluatedAt: "更新",
    coachRecent: "直近の記録",
    coachQuestions: "後で分析できること",
    recommendationTitle: "次回トレーニング提案",
    back: "戻る",
    recommendationSummary: "トレーニングの振り返りと次回の提案を見る",
    exerciseSwitcher: "種目を切り替える",
    previousExercise: "前の種目",
    nextExercise: "次の種目",
    fullRange: "範囲を広げる",
    nearbyRange: "通常の範囲",
    notSet: "未設定",
    completedSets: "記録済みセット",
    effortLevel: "きつさ",
    remainingReps: "残り回数",
    trainingReference: "前回の記録・メモ",
    recommendationObservations: "観察",
    recommendationSuggestions: "提案",
    planTitle: "コーチのトレーニング予定",
    planBadge: "コーチ設定済み",
    planEdit: "予定を調整",
    planDoneEditing: "調整を終了",
    planSave: "予定を保存",
    planAddExercise: "予定種目を追加",
    planRemoveExercise: "種目を削除",
    planStart: "この予定で開始",
    planCoachNotes: "コーチメモ",
    planStartTime: "予定時刻",
    planTargetSets: "目標セット",
    planTargetRepsMin: "最低回数",
    planTargetRepsMax: "最高回数",
    planTargetWeight: "目標重量 kg",
    planTargetDuration: "目標時間 分",
    planTargetDistance: "目標距離 m",
    planTargetRpe: "目標 RPE",
    planTargetRir: "目標 RIR",
    planRestSeconds: "休憩 秒",
    planExerciseNotes: "種目メモ",
    planEmpty: "予定に種目がありません。",
    planSaveExercise: "種目を保存",
    startSession: "開始",
    finishSession: "終了",
    todayCompleted: "今日のトレーニングは終了しました",
    trainingDetail: "トレーニング詳細",
    backToHistory: "履歴に戻る",
    viewDetail: "詳細を見る",
    ended: "終了済み",
    inProgress: "進行中",
    exerciseCount: "種目",
    setCount: "セット",
    noSets: "セット記録はまだありません",
    fatigue: "疲労",
    pain: "痛み",
    notes: "メモ",
    sessionNotes: "今回のトレーニングメモ",
    exerciseNotes: "種目メモ",
    notePlaceholder: "感覚、質問、次回確認したいことを記録",
    saveNote: "メモを保存",
    nextExerciseAdvice: "次回の種目提案",
    evaluationPending: "バックエンドレビュー待ち",
    addExercise: "種目を追加",
    chooseExercise: "種目を選択",
    muscleFilter: "部位で絞り込み",
    allBodyParts: "全部位",
    addSet: "セットを記録",
    copyLastSet: "今回の前セットをコピー",
    previousWorkout: "前回のトレーニング",
    noPreviousWorkout: "前回の記録なし",
    restTimer: "セット間休憩",
    startRest: "休憩を開始",
    pauseRest: "一時停止",
    resumeRest: "再開",
    resetRest: "リセット",
    closeRest: "タイマーを閉じる",
    restFinished: "休憩終了",
    subtract15: "15 秒減らす",
    add15: "15 秒追加",
    weightKg: "重量 kg",
    reps: "回数",
    durationMin: "時間 分",
    distanceM: "距離 m",
    rpe: "RPE",
    rir: "RIR",
    rpeHint: "きつさ 1-10",
    rirHint: "残り回数",
    noSession: "今日はまだ開始していません。",
    noHistory: "記録はまだありません。",
    noMetrics: "体重記録はまだありません。",
    week: "今週",
    low: "不足",
    ok: "範囲内",
    high: "多め",
    bodyWeight: "体重 kg",
    save: "保存",
    description: "種目説明",
    setup: "準備姿勢",
    execution: "動作手順",
    cues: "ポイント",
    cautions: "注意",
    commonMistakes: "よくあるミス",
    image: "種目画像",
    oneHourHint: "1日のトレーニング枠：最大1時間",
    language: "言語",
    error: "リクエスト失敗",
    loading: "読み込み中"
  }
} satisfies Record<Language, Record<string, string>>;

const languageKey = "fitness.language";
const restTimerKey = "fitness.restTimer.v1";

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function sessionDurationMinutes(session: TrainingSession, now = Date.now()): number {
  const parseServerDate = (value: string) => Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? `${value.replace(" ", "T")}Z` : value);
  const started = parseServerDate(session.startedAt);
  const ended = session.endedAt ? parseServerDate(session.endedAt) : now;
  if (!Number.isFinite(started) || !Number.isFinite(ended)) return 0;
  return Math.max(0, Math.round((ended - started) / 60_000));
}

function inputValue(value: number | null): string {
  return value === null ? "" : String(value);
}

function formatEvaluationTime(value: string, language: Language): string {
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value.replace("T", " ").replace("Z", "").slice(0, 16);
  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Tokyo"
  }).format(date);
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...init.headers
    }
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: response.statusText }))) as { error?: string };
    throw new Error(body.error ?? response.statusText);
  }
  return response.json() as Promise<T>;
}

function nameForExercise(exercise: Exercise | { exerciseNameZh: string; exerciseNameJa: string | null }, language: Language) {
  if ("nameZh" in exercise) return language === "ja-JP" && exercise.nameJa ? exercise.nameJa : exercise.nameZh;
  return language === "ja-JP" && exercise.exerciseNameJa ? exercise.exerciseNameJa : exercise.exerciseNameZh;
}

function App() {
  const [language, setLanguage] = React.useState<Language>(() => {
    const saved = localStorage.getItem(languageKey);
    return saved === "ja-JP" ? "ja-JP" : "zh-CN";
  });
  const t = messages[language];
  const [authenticated, setAuthenticated] = React.useState<boolean | null>(null);
  const [view, setView] = React.useState<View>("train");
  const [exercises, setExercises] = React.useState<Exercise[]>([]);
  const [sessions, setSessions] = React.useState<TrainingSession[]>([]);
  const [activeSession, setActiveSession] = React.useState<TrainingSession | null>(null);
  const [todaySession, setTodaySession] = React.useState<TrainingSession | null>(null);
  const [selectedSessionId, setSelectedSessionId] = React.useState<number | null>(null);
  const [weeklyVolume, setWeeklyVolume] = React.useState<WeeklyVolume | null>(null);
  const [bodyMetrics, setBodyMetrics] = React.useState<BodyMetric[]>([]);
  const [recommendation, setRecommendation] = React.useState<NextSessionRecommendation | null>(null);
  const [upcomingPlan, setUpcomingPlan] = React.useState<TrainingPlan | null>(null);
  const [recoveryWindow, setRecoveryWindow] = React.useState<RecoveryWindow | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [online, setOnline] = React.useState(() => navigator.onLine);
  const [installPrompt, setInstallPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [serviceWorkerUpdate, setServiceWorkerUpdate] = React.useState<ServiceWorkerRegistration | null>(null);
  const [restTimer, setRestTimer] = React.useState<RestTimerState | null>(() =>
    parseRestTimer(localStorage.getItem(restTimerKey))
  );
  const selectedSession = sessions.find((session) => session.id === selectedSessionId) ?? null;

  React.useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(languageKey, language);
  }, [language]);

  React.useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    void registerServiceWorker(setServiceWorkerUpdate).catch(() => undefined);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
    };
  }, []);

  React.useEffect(() => {
    if (restTimer) localStorage.setItem(restTimerKey, JSON.stringify(restTimer));
    else localStorage.removeItem(restTimerKey);
  }, [restTimer]);

  React.useEffect(() => {
    if (!restTimer) return;
    const intervalId = window.setInterval(() => {
      setRestTimer((current) => (current ? reconcileRestTimer(current) : null));
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [restTimer?.exerciseId, restTimer?.status]);

  React.useEffect(() => {
    if (!loading && authenticated && restTimer && activeSession?.id !== restTimer.sessionId) {
      setRestTimer(null);
    }
  }, [activeSession?.id, authenticated, loading, restTimer?.sessionId]);

  const refresh = React.useCallback(async () => {
    setError(null);
    const [exerciseData, sessionsData, volumeData, metricsData, recommendationData, recoveryData, planData] = await Promise.all([
      api<{ exercises: Exercise[] }>("/api/exercises"),
      api<{ sessions: TrainingSession[] }>("/api/sessions"),
      api<{ weeklyVolume: WeeklyVolume }>("/api/analytics/weekly-volume"),
      api<{ bodyMetrics: BodyMetric[] }>("/api/body-metrics"),
      api<{ recommendation: NextSessionRecommendation }>("/api/recommendations/next-session"),
      api<{ recoveryWindow: RecoveryWindow | null }>("/api/recovery/today"),
      api<{ trainingPlan: TrainingPlan | null }>("/api/training-plans/upcoming")
    ]);
    const currentTodaySession = sessionsData.sessions.find((session) => session.trainedOn === getLocalDateString()) ?? null;
    setExercises(exerciseData.exercises);
    setSessions(sessionsData.sessions);
    setTodaySession(currentTodaySession);
    setActiveSession(currentTodaySession && !currentTodaySession.endedAt ? currentTodaySession : null);
    setWeeklyVolume(volumeData.weeklyVolume);
    setBodyMetrics(metricsData.bodyMetrics);
    setRecommendation(recommendationData.recommendation);
    setRecoveryWindow(recoveryData.recoveryWindow);
    setUpcomingPlan(planData.trainingPlan);
  }, []);

  React.useEffect(() => {
    void api<{ ok: boolean }>("/api/me")
      .then(async () => {
        setAuthenticated(true);
        await refresh();
      })
      .catch(() => setAuthenticated(false))
      .finally(() => setLoading(false));
  }, [refresh]);

  async function run(action: () => Promise<void>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t.error);
    }
  }

  async function logout() {
    await run(async () => {
      await api("/api/logout", { method: "POST", body: "{}" });
      setRestTimer(null);
      setAuthenticated(false);
    });
  }

  function startRestTimer(sessionId: number, exerciseId: number, exerciseName: string, durationSeconds: number) {
    if (durationSeconds <= 0) return;
    setRestTimer(createRestTimer(sessionId, exerciseId, exerciseName, durationSeconds));
  }

  if (loading || authenticated === null) return <Shell language={language} setLanguage={setLanguage} t={t}>{t.loading}</Shell>;
  if (!authenticated) {
    return (
      <Shell language={language} setLanguage={setLanguage} t={t}>
        <Login
          t={t}
          onLogin={async (password) => {
            await run(async () => {
              await api("/api/login", { method: "POST", body: JSON.stringify({ password }) });
              setAuthenticated(true);
              await refresh();
            });
          }}
          error={error}
        />
      </Shell>
    );
  }

  return (
    <Shell language={language} setLanguage={setLanguage} t={t} onLogout={logout} hasRestTimer={Boolean(restTimer)}>
      {error ? <div className="notice">{error}</div> : null}
      {!online ? <div className="offline-notice" role="status"><WifiOff size={17} /> {t.offline}</div> : null}
      <nav className="tabs" aria-label="Primary">
        <Tab accent="train" active={isPrimaryViewActive(view, "train")} onClick={() => setView("train")} icon={<Dumbbell size={20} />} label={t.trainNav} />
        <Tab accent="insights" active={isPrimaryViewActive(view, "insights")} onClick={() => setView("insights")} icon={<BarChart3 size={20} />} label={t.insightsNav} />
        <Tab accent="history" active={isPrimaryViewActive(view, "history")} onClick={() => setView("history")} icon={<History size={20} />} label={t.history} />
        <Tab accent="profile" active={isPrimaryViewActive(view, "profile")} onClick={() => setView("profile")} icon={<UserRound size={20} />} label={t.profileNav} />
      </nav>
      <div className={`view-shell view-${primaryViewFor(view)}`}>
      {view === "train" ? (
        <TrainView
          t={t}
          language={language}
          exercises={exercises}
          sessions={sessions}
          upcomingPlan={upcomingPlan}
          activeSession={activeSession}
          todaySession={todaySession}
          onStartRestTimer={startRestTimer}
          refresh={refresh}
          run={run}
        />
      ) : null}
      {view === "insights" && weeklyVolume ? (
        <InsightsView
          t={t}
          language={language}
          weeklyVolume={weeklyVolume}
          recommendation={recommendation}
          recoveryWindow={recoveryWindow}
          sessions={sessions}
          refresh={refresh}
          run={run}
        />
      ) : null}
      {view === "history" ? (
        <HistoryView
          t={t}
          language={language}
          sessions={sessions}
          onOpenSession={(sessionId) => {
            setSelectedSessionId(sessionId);
            setView("history-detail");
          }}
        />
      ) : null}
      {view === "history-detail" && selectedSession ? (
        <SessionDetail
          t={t}
          language={language}
          session={selectedSession}
          title={t.trainingDetail}
          onBack={() => setView("history")}
          refresh={refresh}
          run={run}
        />
      ) : null}
      {view === "history-detail" && !selectedSession ? (
        <HistoryView
          t={t}
          language={language}
          sessions={sessions}
          onOpenSession={(sessionId) => {
            setSelectedSessionId(sessionId);
            setView("history-detail");
          }}
        />
      ) : null}
      {view === "profile" ? (
        <ProfileView
          t={t}
          language={language}
          setLanguage={setLanguage}
          bodyMetrics={bodyMetrics}
          installAvailable={Boolean(installPrompt)}
          onInstall={async () => {
            if (!installPrompt) return;
            await installPrompt.prompt();
            await installPrompt.userChoice;
            setInstallPrompt(null);
          }}
          serviceWorkerUpdate={serviceWorkerUpdate}
          onUpdate={() => {
            if (!serviceWorkerUpdate) return;
            navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
            activateServiceWorkerUpdate(serviceWorkerUpdate);
          }}
          onLogout={logout}
          refresh={refresh}
          run={run}
        />
      ) : null}
      </div>
      {restTimer ? (
        <RestTimerBar
          t={t}
          timer={restTimer}
          onAdjust={(seconds) => setRestTimer((current) => (current ? adjustRestTimer(current, seconds) : null))}
          onPause={() => setRestTimer((current) => (current ? pauseRestTimer(current) : null))}
          onResume={() => setRestTimer((current) => (current ? resumeRestTimer(current) : null))}
          onReset={() => setRestTimer((current) => (current ? resetRestTimer(current) : null))}
          onClose={() => setRestTimer(null)}
        />
      ) : null}
    </Shell>
  );
}

function Shell({
  children,
  language,
  setLanguage,
  t,
  onLogout,
  hasRestTimer = false
}: {
  children: React.ReactNode;
  language: Language;
  setLanguage: (language: Language) => void;
  t: Record<string, string>;
  onLogout?: () => void;
  hasRestTimer?: boolean;
}) {
  return (
    <main className={hasRestTimer ? "app has-rest-timer" : "app"}>
      {onLogout ? (
        <header className="session-toolbar">
          <a
            className="apk-download-button"
            href="/api/android/download"
            aria-label={t.downloadAndroid}
            title={t.downloadAndroid}
          >
            <Download size={18} />
            <span>APK</span>
          </a>
        </header>
      ) : <header className="topbar">
        <div>
          <div className="eyebrow"><Activity size={14} /> {t.oneHourHint}</div>
          <h1>{t.appTitle}</h1>
        </div>
        <div className="top-actions">
          <button
            className="icon-button"
            type="button"
            aria-label={t.language}
            title={t.language}
            onClick={() => setLanguage(language === "zh-CN" ? "ja-JP" : "zh-CN")}
          >
            <Globe2 size={19} />
            <span>{language === "zh-CN" ? "中" : "日"}</span>
          </button>
        </div>
      </header>}
      {children}
    </main>
  );
}

function Login({
  t,
  onLogin,
  error
}: {
  t: Record<string, string>;
  onLogin: (password: string) => Promise<void>;
  error: string | null;
}) {
  const [password, setPassword] = React.useState("");
  return (
    <form
      className="panel login-panel"
      onSubmit={(event) => {
        event.preventDefault();
        void onLogin(password);
      }}
    >
      <label>
        <span>{t.password}</span>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus />
      </label>
      {error ? <div className="notice">{error}</div> : null}
      <button className="primary" type="submit">{t.login}</button>
    </form>
  );
}

function Tab({ active, accent, onClick, icon, label }: { active: boolean; accent: "train" | "insights" | "history" | "profile"; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button className={active ? `tab active ${accent}` : `tab ${accent}`} type="button" onClick={onClick} aria-current={active ? "page" : undefined}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function RestTimerBar({
  t,
  timer,
  onAdjust,
  onPause,
  onResume,
  onReset,
  onClose
}: {
  t: Record<string, string>;
  timer: RestTimerState;
  onAdjust: (seconds: number) => void;
  onPause: () => void;
  onResume: () => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const progress = Math.min(100, Math.max(0, (timer.remainingSeconds / timer.durationSeconds) * 100));
  return (
    <aside className={`rest-timer-bar ${timer.status}`} role="dialog" aria-modal="true" aria-live="polite" aria-label={t.restTimer}>
      <div className="rest-timer-head">
        <span>{t.restTimer}</span>
        <button className="timer-icon-button" type="button" aria-label={t.closeRest} title={t.closeRest} onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div
        className="rest-timer-dial"
        style={{ "--timer-progress": `${progress * 3.6}deg` } as React.CSSProperties}
      >
        <div>
          <strong>{timer.status === "finished" ? t.restFinished : formatCountdown(timer.remainingSeconds)}</strong>
          <span>{t.restTimer}</span>
        </div>
      </div>
      <div className="rest-timer-exercise"><strong>{timer.exerciseName}</strong></div>
      <div className="rest-timer-controls">
        <button type="button" aria-label={t.subtract15} title={t.subtract15} onClick={() => onAdjust(-15)}>
          -15s
        </button>
        {timer.status === "running" ? (
          <button className="timer-icon-button" type="button" aria-label={t.pauseRest} title={t.pauseRest} onClick={onPause}>
            <Pause size={18} aria-hidden="true" />
          </button>
        ) : timer.status === "paused" ? (
          <button className="timer-icon-button" type="button" aria-label={t.resumeRest} title={t.resumeRest} onClick={onResume}>
            <Play size={18} aria-hidden="true" />
          </button>
        ) : null}
        <button className="timer-icon-button" type="button" aria-label={t.resetRest} title={t.resetRest} onClick={onReset}>
          <RotateCcw size={18} aria-hidden="true" />
        </button>
        <button type="button" aria-label={t.add15} title={t.add15} onClick={() => onAdjust(15)}>
          +15s
        </button>
      </div>
      <button className="timer-skip" type="button" onClick={onClose}>{t.closeRest}</button>
    </aside>
  );
}

function TrainView({
  t,
  language,
  exercises,
  sessions,
  upcomingPlan,
  activeSession,
  todaySession,
  onStartRestTimer,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  exercises: Exercise[];
  sessions: TrainingSession[];
  upcomingPlan: TrainingPlan | null;
  activeSession: TrainingSession | null;
  todaySession: TrainingSession | null;
  onStartRestTimer: (sessionId: number, exerciseId: number, exerciseName: string, durationSeconds: number) => void;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [selectedExerciseIds, setSelectedExerciseIds] = React.useState<number[]>([]);
  const [addingExercises, setAddingExercises] = React.useState(false);
  const [pickerError, setPickerError] = React.useState<string | null>(null);
  const addingRef = React.useRef(false);
  const [muscleFilter, setMuscleFilter] = React.useState("");
  const [exerciseSearch, setExerciseSearch] = React.useState("");
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [detailExerciseId, setDetailExerciseId] = React.useState<number | null>(null);
  const [fatigueScore, setFatigueScore] = React.useState(3);
  const [painScore, setPainScore] = React.useState(0);
  const [notes, setNotes] = React.useState("");
  const addedExerciseIds = new Set(activeSession?.exercises.map(item => item.exerciseId) ?? []);
  const pendingExerciseIds = selectedExerciseIds.filter(id => !addedExerciseIds.has(id));
  function toggleExercise(id: number) {
    if (addingRef.current || addedExerciseIds.has(id)) return;
    setSelectedExerciseIds(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
    setPickerError(null);
  }
  const detailExercise = exercises.find((exercise) => exercise.id === detailExerciseId) ?? null;
  const muscleOptions = React.useMemo(() => {
    const bySlug = new Map<string, { slug: string; nameZh: string; nameJa: string }>();
    for (const exercise of exercises) {
      for (const muscle of exercise.muscles) {
        bySlug.set(muscle.slug, { slug: muscle.slug, nameZh: muscle.nameZh, nameJa: muscle.nameJa });
      }
    }
    return Array.from(bySlug.values()).sort((a, b) =>
      (language === "ja-JP" ? a.nameJa : a.nameZh).localeCompare(language === "ja-JP" ? b.nameJa : b.nameZh)
    );
  }, [exercises, language]);
  const filteredExercises = React.useMemo(
    () =>
      exercises.filter((exercise) => {
        const matchesMuscle = !muscleFilter || exercise.muscles.some((muscle) => muscle.slug === muscleFilter);
        const searchable = `${exercise.nameZh} ${exercise.nameJa ?? ""} ${exercise.slug}`.toLocaleLowerCase();
        return matchesMuscle && searchable.includes(exerciseSearch.trim().toLocaleLowerCase());
      }),
    [exerciseSearch, exercises, muscleFilter]
  );

  React.useEffect(() => {
    if (activeSession) setNotes(activeSession.notes ?? "");
  }, [activeSession?.id, activeSession?.notes]);

  React.useEffect(() => {
    setSelectedExerciseIds([]);
    setPickerOpen(false);
    setPickerError(null);
  }, [activeSession?.id]);

  if (!activeSession) {
    if (todaySession?.endedAt) {
      return (
        <div className="stack">
          {upcomingPlan ? (
            <TrainingPlanCard
              t={t}
              language={language}
              plan={upcomingPlan}
              exercises={exercises}
              fatigueScore={fatigueScore}
              painScore={painScore}
              notes={notes}
              onFatigueChange={setFatigueScore}
              onPainChange={setPainScore}
              onNotesChange={setNotes}
              refresh={refresh}
              run={run}
            />
          ) : null}
          <SessionDetail
            t={t}
            language={language}
            session={todaySession}
            title={t.todayCompleted}
            refresh={refresh}
            run={run}
          />
        </div>
      );
    }

    return (
      <section className="stack">
        {upcomingPlan ? (
          <TrainingPlanCard
            t={t}
            language={language}
            plan={upcomingPlan}
            exercises={exercises}
            fatigueScore={fatigueScore}
            painScore={painScore}
            notes={notes}
            onFatigueChange={setFatigueScore}
            onPainChange={setPainScore}
            onNotesChange={setNotes}
            refresh={refresh}
            run={run}
          />
        ) : (
          <div className="panel">
            <p className="muted">{t.noSession}</p>
            <Slider label={t.fatigue} value={fatigueScore} onChange={setFatigueScore} />
            <Slider label={t.pain} value={painScore} onChange={setPainScore} />
            <label>
              <span>{t.notes}</span>
              <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
            </label>
            <button
              className="primary"
              type="button"
              onClick={() =>
                void run(async () => {
                  await api("/api/sessions", {
                    method: "POST",
                    body: JSON.stringify({ trainedOn: getLocalDateString(), fatigueScore, painScore, notes })
                  });
                  await refresh();
                })
              }
            >
              <Timer size={18} /> {t.startSession}
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="stack active-training">
      <div className="panel compact active-session-summary">
        <div className="session-head">
          <div>
            <span className="eyebrow">{t.elapsed}</span>
            <strong className="session-duration">{sessionDurationMinutes(activeSession)} min</strong>
          </div>
          <button
            className="secondary destructive"
            type="button"
            onClick={() =>
              window.confirm(t.finishConfirm) ? void run(async () => {
                await api(`/api/sessions/${activeSession.id}`, {
                  method: "PATCH",
                  body: JSON.stringify({ ended: true, fatigueScore, painScore, notes })
                });
                await refresh();
              }) : undefined
            }
          >
            {t.finishSession}
          </button>
        </div>
      </div>
      <button className="exercise-picker-trigger" type="button" onClick={() => setPickerOpen(true)}>
        <Plus size={20} aria-hidden="true" />
        <span>{t.chooseExercises}</span>
        <ChevronRight size={19} aria-hidden="true" />
      </button>
      {pickerOpen ? (
        <div className="modal-backdrop" role="presentation">
          <section className="exercise-picker" role="dialog" aria-modal="true" aria-labelledby="exercise-picker-title">
            <header>
              <h2 id="exercise-picker-title">{t.chooseExercises}</h2>
              <button className="icon-button" type="button" aria-label={t.closeDetails} disabled={addingExercises} onClick={() => setPickerOpen(false)}><X size={20} /></button>
            </header>
            <label className="search-field">
              <Search size={18} aria-hidden="true" />
              <input value={exerciseSearch} onChange={(event) => setExerciseSearch(event.target.value)} placeholder={t.exerciseSearch} disabled={addingExercises} />
            </label>
            <div className="filter-chips" role="group" aria-label={t.muscleFilter}>
              <button className={!muscleFilter ? "selected" : ""} type="button" disabled={addingExercises} onClick={() => setMuscleFilter("")}>{t.allBodyParts}</button>
              {muscleOptions.map((muscle) => (
                <button className={muscleFilter === muscle.slug ? "selected" : ""} type="button" key={muscle.slug} disabled={addingExercises} onClick={() => setMuscleFilter(muscle.slug)}>
                  {language === "ja-JP" ? muscle.nameJa : muscle.nameZh}
                </button>
              ))}
            </div>
            <div className="exercise-option-list">
              {filteredExercises.map(exercise => {
                const order = pendingExerciseIds.indexOf(exercise.id);
                const added = addedExerciseIds.has(exercise.id);
                return (
                  <div className={`exercise-option${order >= 0 ? " selected" : ""}${added ? " already-added" : ""}`} key={exercise.id}>
                    <label className="exercise-option-main">
                      <input type="checkbox" aria-label={nameForExercise(exercise, language)} checked={added || order >= 0}
                        disabled={added || addingExercises} onChange={() => toggleExercise(exercise.id)} />
                      <span className="exercise-option-copy"><strong>{nameForExercise(exercise, language)}</strong>
                        <span>{exercise.muscles.map(muscle => language === "ja-JP" ? muscle.nameJa : muscle.nameZh).join(" · ")}</span>
                      </span>
                      {added ? <span className="exercise-added-label">{t.alreadyAdded}</span> : order >= 0 ? <span className="selection-order" aria-hidden="true">{order + 1}</span> : null}
                    </label>
                    <button className="exercise-info" type="button" disabled={addingExercises} aria-label={`${nameForExercise(exercise, language)} ${t.description}`} onClick={() => setDetailExerciseId(exercise.id)}><Info size={18} /></button>
                  </div>
                );
              })}
              {!filteredExercises.length ? <p className="muted">{t.noMatchingExercises}</p> : null}
            </div>
            <footer className="picker-footer">
              {pendingExerciseIds.length > 0 ? <>
                <div className="picker-selection-head"><span>{t.selectedOrder}</span>
                  <button type="button" disabled={addingExercises} onClick={() => { setSelectedExerciseIds([]); setPickerError(null); }}>{t.clearSelection}</button>
                </div>
                <div className="picker-selected-list" aria-label={t.selectedOrder}>
                  {pendingExerciseIds.map((id, index) => {
                    const exercise = exercises.find(item => item.id === id)!;
                    return <button type="button" key={id} disabled={addingExercises} aria-label={`${t.removeSelection} ${nameForExercise(exercise, language)}`} onClick={() => toggleExercise(id)}>
                      <span>{index + 1}</span>{nameForExercise(exercise, language)}<X size={14} aria-hidden="true" />
                    </button>;
                  })}
                </div>
              </> : null}
              {pickerError ? <p className="picker-error" role="alert">{pickerError}</p> : null}
            <button
              className="primary picker-confirm"
              type="button"
              disabled={!pendingExerciseIds.length || addingExercises}
              onClick={async () => {
                if (addingRef.current) return;
                addingRef.current = true;
                setAddingExercises(true);
                setPickerError(null);
                try {
                  await api(`/api/sessions/${activeSession.id}/exercises`, { method: "POST", body: JSON.stringify({ exerciseIds: pendingExerciseIds }) });
                  await refresh();
                  setSelectedExerciseIds([]);
                  setPickerOpen(false);
                } catch (error) {
                  setPickerError(error instanceof Error ? error.message : t.error);
                } finally {
                  addingRef.current = false;
                  setAddingExercises(false);
                }
              }}
            >
              {addingExercises ? t.addingExercises : `${t.addSelected} (${pendingExerciseIds.length})`}
            </button>
            </footer>
            {detailExercise ? (
              <div className="exercise-detail-sheet">
                <button className="icon-button detail-sheet-close" type="button" aria-label={t.closeDetails} onClick={() => setDetailExerciseId(null)}><X size={20} /></button>
                <ExerciseDetails t={t} language={language} exercise={detailExercise} />
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
      <ExerciseCarousel key={activeSession.id} t={t} language={language} items={activeSession.exercises}>
      {activeSession.exercises.map((item) => (
        <SessionExerciseCard
          key={item.id}
          t={t}
          language={language}
          sessionId={activeSession.id}
          item={item}
          previous={findPreviousExercise(sessions, activeSession.id, item.exerciseId)}
          defaultRestSeconds={exercises.find((exercise) => exercise.id === item.exerciseId)?.defaultRestSeconds ?? 0}
          onStartRestTimer={onStartRestTimer}
          refresh={refresh}
          run={run}
        />
      ))}
      </ExerciseCarousel>
      <details className="training-disclosure session-note-disclosure"><summary>{t.sessionNotes}<ChevronDown size={18} aria-hidden="true" /></summary>
        <NoteEditor t={t} label={t.sessionNotes} value={notes} onChange={setNotes}
          onSave={() => run(async () => {
            await api(`/api/sessions/${activeSession.id}`, { method: "PATCH", body: JSON.stringify({ notes }) });
            await refresh();
          })} />
      </details>
    </section>
  );
}

function ExerciseCarousel({ t, language, items, children }: {
  t: Record<string, string>;
  language: Language;
  items: TrainingSession["exercises"];
  children: React.ReactNode;
}) {
  const [activeId, setActiveId] = React.useState(items[0]?.id);
  const viewport = React.useRef<HTMLDivElement>(null);
  const chips = React.useRef<HTMLDivElement>(null);
  const dragStart = React.useRef<{ x: number; y: number; pointerId: number; horizontal: boolean } | null>(null);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const previousIds = React.useRef(items.map(item => item.id));
  const activeIndex = Math.max(0, items.findIndex(item => item.id === activeId));
  const slides = React.Children.toArray(children);
  const idsKey = items.map(item => item.id).join(",");

  // All cards stay mounted so switching never discards an unsaved field.
  function select(index: number) {
    const item = items[index];
    if (!item || !viewport.current) return;
    setActiveId(item.id);
  }

  function resetDrag() {
    dragStart.current = null;
    setDragOffset(0);
    setDragging(false);
  }

  function moveDrag(event: React.PointerEvent<HTMLDivElement>) {
    const start = dragStart.current;
    if (!start || event.pointerId !== start.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (!start.horizontal) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
      if (Math.abs(dx) <= Math.abs(dy) * 1.3) {
        resetDrag();
        event.currentTarget.releasePointerCapture(event.pointerId);
        return;
      }
      start.horizontal = true;
      setDragging(true);
    }
    const atEdge = (activeIndex === 0 && dx > 0) || (activeIndex === items.length - 1 && dx < 0);
    const width = event.currentTarget.clientWidth;
    setDragOffset(Math.max(-width, Math.min(width, dx)) * (atEdge ? 0.25 : 1));
  }

  function finishDrag(event: React.PointerEvent<HTMLDivElement>) {
    const start = dragStart.current;
    if (!start || event.pointerId !== start.pointerId) return;
    const dx = event.clientX - start.x;
    if (start.horizontal && Math.abs(dx) >= Math.min(80, event.currentTarget.clientWidth * 0.2)) {
      select(activeIndex + (dx < 0 ? 1 : -1));
    }
    resetDrag();
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  React.useLayoutEffect(() => {
    const added = items.filter(item => !previousIds.current.includes(item.id));
    previousIds.current = items.map(item => item.id);
    const index = added.length ? items.indexOf(added[added.length - 1]) : activeIndex;
    select(index);
  }, [idsKey]);

  React.useLayoutEffect(() => {
    const container = viewport.current;
    const slide = container?.children[activeIndex] as HTMLElement | undefined;
    if (!container || !slide) return;
    const resize = () => { container.style.height = `${slide.getBoundingClientRect().height + 4}px`; };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(slide);
    const chip = chips.current?.children[activeIndex] as HTMLElement | undefined;
    if (chip && chips.current) {
      const row = chips.current;
      if (chip.offsetLeft < row.scrollLeft) row.scrollLeft = chip.offsetLeft;
      else if (chip.offsetLeft + chip.offsetWidth > row.scrollLeft + row.clientWidth) {
        row.scrollLeft = chip.offsetLeft + chip.offsetWidth - row.clientWidth;
      }
    }
    return () => observer.disconnect();
  }, [activeIndex, idsKey]);

  if (!items.length) return null;
  return (
    <section className="exercise-carousel" aria-label={t.exerciseSwitcher}>
      <div className="exercise-carousel-tabs" ref={chips} role="group" aria-label={t.exerciseSwitcher}>
        {items.map((item, index) => (
          <button type="button" key={item.id} aria-pressed={index === activeIndex}
            onClick={() => select(index)}>{nameForExercise(item, language)}</button>
        ))}
      </div>
      <div className="exercise-carousel-navigation">
        <button type="button" aria-label={t.previousExercise} disabled={activeIndex === 0}
          onClick={() => select(activeIndex - 1)}><ArrowLeft size={18} aria-hidden="true" /></button>
        <span aria-live="polite">{activeIndex + 1} / {items.length}</span>
        <button type="button" aria-label={t.nextExercise} disabled={activeIndex === items.length - 1}
          onClick={() => select(activeIndex + 1)}><ChevronRight size={20} aria-hidden="true" /></button>
      </div>
      <div className={`exercise-carousel-viewport${dragging ? " is-dragging" : ""}`} ref={viewport}
        onPointerDown={event => {
          // Sliders and other controls own their gestures; blank card areas switch exercises.
          const target = event.target as HTMLElement;
          if (!event.isPrimary) { resetDrag(); return; }
          if (event.button !== 0 || target.closest("input, textarea, button, summary, a, label")) return;
          dragStart.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId, horizontal: false };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={moveDrag} onPointerUp={finishDrag}
        onPointerCancel={resetDrag} onLostPointerCapture={resetDrag}>
        {items.map((item, index) => (
          <div className="exercise-carousel-slide" key={item.id} inert={index !== activeIndex}
            style={{ transform: `translateX(calc(-${activeIndex * 100}% + ${dragOffset}px))` }}
            aria-hidden={index !== activeIndex}>{slides[index]}</div>
        ))}
      </div>
    </section>
  );
}

function planExerciseSummary(item: TrainingPlanExercise, t: Record<string, string>): string {
  const parts = [`${item.targetSets} ${t.setCount}`];
  if (item.targetWeightKg !== null) parts.push(`${item.targetWeightKg} kg`);
  if (item.targetRepsMin !== null || item.targetRepsMax !== null) {
    const minimum = item.targetRepsMin ?? item.targetRepsMax;
    const maximum = item.targetRepsMax ?? item.targetRepsMin;
    parts.push(minimum === maximum ? `${minimum} ${t.reps}` : `${minimum}-${maximum} ${t.reps}`);
  }
  if (item.targetDurationSeconds !== null) parts.push(`${Number((item.targetDurationSeconds / 60).toFixed(1))} min`);
  if (item.targetDistanceMeters !== null) parts.push(`${item.targetDistanceMeters} m`);
  if (item.targetRpe !== null) parts.push(`RPE ${item.targetRpe}`);
  if (item.targetRir !== null) parts.push(`RIR ${item.targetRir}`);
  if (item.restSeconds > 0) parts.push(`${item.restSeconds}s ${t.restTimer}`);
  return parts.join(" · ");
}

function TrainingPlanCard({
  t,
  language,
  plan,
  exercises,
  fatigueScore,
  painScore,
  notes,
  onFatigueChange,
  onPainChange,
  onNotesChange,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  plan: TrainingPlan;
  exercises: Exercise[];
  fatigueScore: number;
  painScore: number;
  notes: string;
  onFatigueChange: (value: number) => void;
  onPainChange: (value: number) => void;
  onNotesChange: (value: string) => void;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [editing, setEditing] = React.useState(false);
  const [title, setTitle] = React.useState(plan.title);
  const [plannedStartTime, setPlannedStartTime] = React.useState(plan.plannedStartTime ?? "");
  const [coachNotes, setCoachNotes] = React.useState(plan.coachNotes ?? "");
  const [newExerciseId, setNewExerciseId] = React.useState<number | "">("");
  const [editingExerciseId, setEditingExerciseId] = React.useState<number | null>(null);
  React.useEffect(() => {
    setTitle(plan.title);
    setPlannedStartTime(plan.plannedStartTime ?? "");
    setCoachNotes(plan.coachNotes ?? "");
  }, [plan.id, plan.title, plan.plannedStartTime, plan.coachNotes]);

  return (
    <article className="panel training-plan-card">
      <div className="training-plan-head">
        <div>
          <span className="training-plan-kicker"><CalendarDays size={16} aria-hidden="true" /> {t.planTitle}</span>
          <h2>{plan.title}</h2>
          <span>{plan.scheduledOn}{plan.plannedStartTime ? ` · ${plan.plannedStartTime}` : ""}</span>
        </div>
        <span className="badge ok">{t.planBadge}</span>
      </div>

      {!editing && plan.coachNotes ? <p className="training-plan-notes">{plan.coachNotes}</p> : null}

      <div className="training-plan-list">
        {plan.exercises.length === 0 ? <p className="muted">{t.planEmpty}</p> : null}
        {plan.exercises.map((item) => (
          <React.Fragment key={item.id}>
            <div className="training-plan-item" key={item.id}>
              <span className="training-plan-position">{item.position}</span>
              <div>
                <strong>{language === "ja-JP" ? item.exerciseNameJa ?? item.exerciseNameZh : item.exerciseNameZh}</strong>
                <span>{planExerciseSummary(item, t)}</span>
                {item.notes ? <small>{item.notes}</small> : null}
              </div>
              {editing ? (
                <button
                  className="icon-button training-plan-item-edit"
                  type="button"
                  aria-label={t.planEdit}
                  title={t.planEdit}
                  onClick={() => setEditingExerciseId((current) => current === item.id ? null : item.id)}
                >
                  <Pencil size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            {editing && editingExerciseId === item.id ? (
              <TrainingPlanExerciseEditor
                t={t}
                language={language}
                item={item}
                exercises={exercises}
                refresh={refresh}
                run={run}
              />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      {editing ? (
        <div className="training-plan-edit-block">
          <label>
            <span>{t.planTitle}</span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label>
            <span>{t.planStartTime}</span>
            <input type="time" value={plannedStartTime} onChange={(event) => setPlannedStartTime(event.target.value)} />
          </label>
          <label>
            <span>{t.planCoachNotes}</span>
            <textarea value={coachNotes} onChange={(event) => setCoachNotes(event.target.value)} rows={3} />
          </label>
          <button
            className="secondary"
            type="button"
            onClick={() =>
              void run(async () => {
                await api(`/api/training-plans/${plan.id}`, {
                  method: "PATCH",
                  body: JSON.stringify({ title, plannedStartTime, coachNotes })
                });
                await refresh();
              })
            }
          >
            <Save size={16} aria-hidden="true" /> {t.planSave}
          </button>
          <div className="training-plan-add">
            <select value={newExerciseId} onChange={(event) => setNewExerciseId(event.target.value ? Number(event.target.value) : "")}>
              <option value="">{t.chooseExercise}</option>
              {exercises.map((exercise) => (
                <option key={exercise.id} value={exercise.id}>{nameForExercise(exercise, language)}</option>
              ))}
            </select>
            <button
              className="primary"
              type="button"
              disabled={!newExerciseId}
              onClick={() =>
                void run(async () => {
                  const exercise = exercises.find((item) => item.id === newExerciseId);
                  if (!exercise) return;
                  const isStrength = exercise.trackingType === "strength";
                  await api(`/api/training-plans/${plan.id}/exercises`, {
                    method: "POST",
                    body: JSON.stringify({
                      exerciseId: exercise.id,
                      targetSets: isStrength ? 3 : 1,
                      targetRepsMin: isStrength ? 8 : null,
                      targetRepsMax: isStrength ? 10 : null,
                      targetDurationSeconds: exercise.trackingType === "timed" ? 300 : null,
                      targetRpe: isStrength ? null : 3,
                      targetRir: isStrength ? 2 : null,
                      restSeconds: exercise.defaultRestSeconds
                    })
                  });
                  setNewExerciseId("");
                  await refresh();
                })
              }
            >
              <Plus size={17} aria-hidden="true" /> {t.planAddExercise}
            </button>
          </div>
        </div>
      ) : null}

      <button
        className="secondary"
        type="button"
        onClick={() => {
          setEditingExerciseId(null);
          setEditing((value) => !value);
        }}
      >
        {editing ? <CheckCircle2 size={17} aria-hidden="true" /> : <Pencil size={17} aria-hidden="true" />}
        {editing ? t.planDoneEditing : t.planEdit}
      </button>

      <div className="training-plan-readiness">
        <Slider label={t.fatigue} value={fatigueScore} onChange={onFatigueChange} />
        <Slider label={t.pain} value={painScore} onChange={onPainChange} />
        <label>
          <span>{t.notes}</span>
          <textarea value={notes} onChange={(event) => onNotesChange(event.target.value)} rows={2} />
        </label>
        <button
          className="primary"
          type="button"
          disabled={plan.exercises.length === 0}
          onClick={() =>
            void run(async () => {
              await api(`/api/training-plans/${plan.id}/start`, {
                method: "POST",
                body: JSON.stringify({ fatigueScore, painScore, notes })
              });
              await refresh();
            })
          }
        >
          <Timer size={18} aria-hidden="true" /> {t.planStart}
        </button>
      </div>
    </article>
  );
}

function TrainingPlanExerciseEditor({
  t,
  language,
  item,
  exercises,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  item: TrainingPlanExercise;
  exercises: Exercise[];
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [exerciseId, setExerciseId] = React.useState(item.exerciseId.toString());
  const [targetSets, setTargetSets] = React.useState(item.targetSets.toString());
  const [targetRepsMin, setTargetRepsMin] = React.useState(item.targetRepsMin?.toString() ?? "");
  const [targetRepsMax, setTargetRepsMax] = React.useState(item.targetRepsMax?.toString() ?? "");
  const [targetWeightKg, setTargetWeightKg] = React.useState(item.targetWeightKg?.toString() ?? "");
  const [targetDurationMin, setTargetDurationMin] = React.useState(
    item.targetDurationSeconds === null ? "" : String(Number((item.targetDurationSeconds / 60).toFixed(1)))
  );
  const [targetDistanceMeters, setTargetDistanceMeters] = React.useState(item.targetDistanceMeters?.toString() ?? "");
  const [targetRpe, setTargetRpe] = React.useState(item.targetRpe?.toString() ?? "");
  const [targetRir, setTargetRir] = React.useState(item.targetRir?.toString() ?? "");
  const [restSeconds, setRestSeconds] = React.useState(item.restSeconds.toString());
  const [itemNotes, setItemNotes] = React.useState(item.notes ?? "");
  const selectedExercise = exercises.find((exercise) => exercise.id === Number(exerciseId));
  const trackingType = selectedExercise?.trackingType ?? item.trackingType;
  const showWeight = trackingType === "strength" || trackingType === "carry" || trackingType === "hold";
  const showReps = trackingType === "strength";
  const showDuration = trackingType === "timed" || trackingType === "carry" || trackingType === "distance" || trackingType === "hold";
  const showDistance = trackingType === "carry" || trackingType === "distance";

  return (
    <div className="training-plan-editor">
      <select value={exerciseId} onChange={(event) => setExerciseId(event.target.value)}>
        {exercises.map((exercise) => (
          <option value={exercise.id} key={exercise.id}>{nameForExercise(exercise, language)}</option>
        ))}
      </select>
      <div className="quick-grid">
        <NumberInput label={t.planTargetSets} value={targetSets} onChange={setTargetSets} />
        {showWeight ? <NumberInput label={t.planTargetWeight} value={targetWeightKg} onChange={setTargetWeightKg} /> : null}
        {showReps ? <NumberInput label={t.planTargetRepsMin} value={targetRepsMin} onChange={setTargetRepsMin} /> : null}
        {showReps ? <NumberInput label={t.planTargetRepsMax} value={targetRepsMax} onChange={setTargetRepsMax} /> : null}
        {showDuration ? <NumberInput label={t.planTargetDuration} value={targetDurationMin} onChange={setTargetDurationMin} /> : null}
        {showDistance ? <NumberInput label={t.planTargetDistance} value={targetDistanceMeters} onChange={setTargetDistanceMeters} /> : null}
        <NumberInput label={t.planTargetRpe} value={targetRpe} onChange={setTargetRpe} />
        {showReps ? <NumberInput label={t.planTargetRir} value={targetRir} onChange={setTargetRir} /> : null}
        <NumberInput label={t.planRestSeconds} value={restSeconds} onChange={setRestSeconds} />
      </div>
      <label>
        <span>{t.planExerciseNotes}</span>
        <textarea value={itemNotes} onChange={(event) => setItemNotes(event.target.value)} rows={2} />
      </label>
      <div className="button-row">
        <button
          className="secondary destructive"
          type="button"
          aria-label={t.planRemoveExercise}
          title={t.planRemoveExercise}
          onClick={() =>
            void run(async () => {
              await api(`/api/training-plan-exercises/${item.id}`, { method: "DELETE" });
              await refresh();
            })
          }
        >
          <Trash2 size={16} aria-hidden="true" /> {t.planRemoveExercise}
        </button>
        <button
          className="primary"
          type="button"
          onClick={() =>
            void run(async () => {
              await api(`/api/training-plan-exercises/${item.id}`, {
                method: "PATCH",
                body: JSON.stringify({
                  exerciseId,
                  targetSets,
                  targetRepsMin,
                  targetRepsMax,
                  targetWeightKg,
                  targetDurationSeconds: targetDurationMin ? Math.round(Number(targetDurationMin) * 60) : "",
                  targetDistanceMeters,
                  targetRpe,
                  targetRir,
                  restSeconds,
                  notes: itemNotes
                })
              });
              await refresh();
            })
          }
        >
          <Save size={16} aria-hidden="true" /> {t.planSaveExercise}
        </button>
      </div>
    </div>
  );
}

function ExerciseDetails({ t, language, exercise }: { t: Record<string, string>; language: Language; exercise: Exercise }) {
  return (
    <div className="exercise-details">
      <h3>{nameForExercise(exercise, language)}</h3>
      {exercise.imageUrl ? (
        <figure className="exercise-figure">
          <img src={exercise.imageUrl} alt={exercise.imageAltZh ?? t.image} loading="lazy" />
          {exercise.imageCredit ? <figcaption>{exercise.imageCredit}</figcaption> : null}
        </figure>
      ) : null}
      {exercise.descriptionZh ? (
        <p>
          <strong>{t.description}</strong>
          {exercise.descriptionZh}
        </p>
      ) : null}
      {exercise.setupZh ? (
        <p>
          <strong>{t.setup}</strong>
          {exercise.setupZh}
        </p>
      ) : null}
      {exercise.executionZh ? (
        <p>
          <strong>{t.execution}</strong>
          {exercise.executionZh}
        </p>
      ) : null}
      {exercise.cuesZh ? (
        <p>
          <strong>{t.cues}</strong>
          {exercise.cuesZh}
        </p>
      ) : null}
      {exercise.cautionsZh ? (
        <p>
          <strong>{t.cautions}</strong>
          {exercise.cautionsZh}
        </p>
      ) : null}
      {exercise.commonMistakesZh ? (
        <p>
          <strong>{t.commonMistakes}</strong>
          {exercise.commonMistakesZh}
        </p>
      ) : null}
    </div>
  );
}

function RecommendationCard({
  t,
  recommendation
}: {
  t: Record<string, string>;
  recommendation: NextSessionRecommendation | null;
}) {
  const [open, setOpen] = React.useState(false);
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const titleId = React.useId();
  const dialogId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    dialog.scrollTop = 0;
    const onBack = () => setOpen(false);
    window.addEventListener("popstate", onBack);
    return () => {
      window.removeEventListener("popstate", onBack);
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!recommendation) return null;
  const summary = recommendation.suggestedItems[0]?.title || recommendation.reason || t.recommendationSummary;
  function showDetails() {
    window.history.pushState({ ...window.history.state, fitnessAdvice: true }, "", window.location.href);
    setOpen(true);
  }
  function goBack() {
    window.history.back();
  }
  return (
    <>
      <button className="recommendation-entry" type="button" onClick={showDetails}
        aria-haspopup="dialog" aria-expanded={open} aria-controls={dialogId}>
        <Lightbulb size={22} aria-hidden="true" />
        <span className="recommendation-entry-copy">
          <strong>{t.recommendationTitle}</strong>
          <span>{summary}</span>
        </span>
        <ChevronRight size={20} aria-hidden="true" />
      </button>
      <dialog ref={dialogRef} id={dialogId} className="recommendation-detail" aria-labelledby={titleId}
        onCancel={(event) => { event.preventDefault(); goBack(); }}>
        <header className="recommendation-detail-header">
          <div>
            <button type="button" className="recommendation-back" onClick={goBack} autoFocus>
              <ArrowLeft size={20} aria-hidden="true" /> {t.back}
            </button>
            <h1 id={titleId}>{t.recommendationTitle}</h1>
          </div>
        </header>
        <div className="recommendation-detail-body">
          {recommendation.reason ? <p className="recommendation-reason">{recommendation.reason}</p> : null}
          <RecommendationContent t={t} recommendation={recommendation} />
        </div>
      </dialog>
    </>
  );
}

function RecommendationContent({
  t,
  recommendation
}: {
  t: Record<string, string>;
  recommendation: NextSessionRecommendation | null;
}) {
  if (!recommendation) return null;
  return (
    <article className="panel recommendation-card">
      <div className="recommendation-head">
        <h2>
          <Lightbulb size={18} aria-hidden="true" />
          {t.recommendationTitle}
        </h2>
      </div>
      {recommendation.observations.length > 0 ? (
        <div className="recommendation-section">
          <h3>{t.recommendationObservations}</h3>
          <div className="recommendation-grid">
            {recommendation.observations.map((observation) => (
              <div className={`recommendation-observation ${observation.tone}`} key={observation.label}>
                <span>{observation.label}</span>
                <strong>{observation.value}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {recommendation.suggestedItems.length > 0 ? (
        <div className="recommendation-section">
          <h3>{t.recommendationSuggestions}</h3>
          <div className="recommendation-items">
            {recommendation.suggestedItems.map((item) => (
              <div className="recommendation-item" key={`${item.exerciseSlug ?? "review"}-${item.title}`}>
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
                <small>{item.reason}</small>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function PreviousPerformance({
  t,
  previous
}: {
  t: Record<string, string>;
  previous: PreviousExerciseRecord | null;
}) {
  if (!previous) return <div className="previous-performance empty">{t.noPreviousWorkout}</div>;

  const item = previous.exercise;
  const isTimed = item.trackingType === "timed";
  const isCarry = item.trackingType === "carry";
  const isDistance = item.trackingType === "distance";
  const isHold = item.trackingType === "hold";
  const showWeight = item.trackingType === "strength" || isCarry || isHold;
  const showReps = item.trackingType === "strength";
  const showDuration = isTimed || isCarry || isDistance || isHold;
  const showDistance = isCarry || isDistance;
  const showRir = item.trackingType === "strength";

  return (
    <div className="previous-performance">
      <div className="previous-performance-head">
        <span>{t.previousWorkout}</span>
        <strong>{previous.trainedOn}</strong>
      </div>
      {item.sets.length ? (
        <div className="previous-set-list">
          {item.sets.map((set) => (
            <div className="previous-set-row" key={set.id}>
              <span>#{set.position}</span>
              {showWeight ? <strong>{set.weightKg ?? "-"} kg</strong> : null}
              {showReps ? <strong>{set.reps ?? "-"} reps</strong> : null}
              {showDuration ? (
                <strong>{set.durationSeconds ? Number((set.durationSeconds / 60).toFixed(1)) : "-"} min</strong>
              ) : null}
              {showDistance ? <strong>{set.distanceMeters ?? "-"} m</strong> : null}
              <span>RPE {set.rpe ?? "-"}</span>
              {showRir ? <span>RIR {set.rir ?? "-"}</span> : null}
            </div>
          ))}
        </div>
      ) : (
        <span className="muted">{t.noSets}</span>
      )}
    </div>
  );
}

function SessionExerciseCard({
  t,
  language,
  sessionId,
  item,
  previous,
  defaultRestSeconds,
  onStartRestTimer,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  sessionId: number;
  item: TrainingSession["exercises"][number];
  previous: PreviousExerciseRecord | null;
  defaultRestSeconds: number;
  onStartRestTimer: (sessionId: number, exerciseId: number, exerciseName: string, durationSeconds: number) => void;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const defaultRpe = "7";
  const defaultRir = "3";
  const lastSet = item.sets.at(-1);
  const initialPrefill = getSetPrefill(item.sets, previous?.exercise.sets ?? [], item.trackingType);
  const showRir = item.trackingType === "strength";
  const [weightKg, setWeightKg] = React.useState(inputValue(initialPrefill.weightKg));
  const [reps, setReps] = React.useState(inputValue(initialPrefill.reps));
  const [durationMin, setDurationMin] = React.useState(
    initialPrefill.durationSeconds ? String(Number((initialPrefill.durationSeconds / 60).toFixed(1))) : ""
  );
  const [distanceM, setDistanceM] = React.useState(inputValue(initialPrefill.distanceMeters));
  const [rpe, setRpe] = React.useState(inputValue(initialPrefill.rpe) || defaultRpe);
  const [rir, setRir] = React.useState(showRir ? inputValue(initialPrefill.rir) || defaultRir : "");
  const [exerciseNotes, setExerciseNotes] = React.useState(item.notes ?? "");
  const fields = getTrackingFields(item.trackingType);
  const showWeight = fields.weight;
  const showReps = fields.reps;
  const showDuration = fields.duration;
  const showDistance = fields.distance;

  React.useEffect(() => {
    setExerciseNotes(item.notes ?? "");
  }, [item.id, item.notes]);

  function copyLastSet() {
    if (!lastSet) return;
    setWeightKg(lastSet.weightKg?.toString() ?? "");
    setReps(lastSet.reps?.toString() ?? "");
    setDurationMin(lastSet.durationSeconds ? String(Number((lastSet.durationSeconds / 60).toFixed(1))) : "");
    setDistanceM(lastSet.distanceMeters?.toString() ?? "");
    setRpe(lastSet.rpe?.toString() ?? defaultRpe);
    setRir(showRir ? lastSet.rir?.toString() ?? defaultRir : "");
  }

  return (
    <article className={`panel training-card type-${item.trackingType}`}>
      <div className="training-card-head">
        <div>
          <span className="tracking-type">{item.trackingType}</span>
          <h2>{nameForExercise(item, language)}</h2>
        </div>
        <span className="set-count-badge">{item.sets.length} {t.setCount}</span>
      </div>
      {initialPrefill.source ? <div className="prefill-source">{initialPrefill.source === "current" ? t.prefillCurrent : t.prefillPrevious}</div> : null}
      <div className="metric-slider-grid">
        {showWeight ? <MetricSlider t={t} label={t.weightKg} value={weightKg} onChange={setWeightKg} step={0.5} initialMax={20} maximum={500} radius={10} baseline={initialPrefill.weightKg} /> : null}
        {showReps ? <MetricSlider t={t} label={t.reps} value={reps} onChange={setReps} step={1} initialMax={20} maximum={100} /> : null}
        {showDuration ? <MetricSlider t={t} label={t.durationMin} value={durationMin} onChange={setDurationMin} step={0.5} fineStep={0.1} initialMax={15} maximum={120} /> : null}
        {showDistance ? <MetricSlider t={t} label={t.distanceM} value={distanceM} onChange={setDistanceM} step={10} fineStep={0.25} initialMax={2000} maximum={100000} /> : null}
      </div>
      <div className="effort-grid">
        <EffortSlider
          label={t.effortLevel}
          hint={t.rpeHint}
          min={1}
          max={10}
          value={rpe}
          onChange={setRpe}
        />
        {showRir ? (
          <EffortSlider
            label={t.remainingReps}
            hint={t.rirHint}
            min={0}
            max={5}
            value={rir}
            onChange={setRir}
          />
        ) : null}
      </div>
      <div className="button-row">
        <button className="secondary" type="button" disabled={!lastSet} onClick={copyLastSet}>
          {t.copyLastSet}
        </button>
        <button
          className="primary"
          type="button"
          onClick={() =>
            void run(async () => {
                const payload = buildSetPayload(item.trackingType, { weightKg, reps, durationMin, distanceM, rpe, rir });
                await api(`/api/session-exercises/${item.id}/sets`, {
                  method: "POST",
                  body: JSON.stringify(payload)
                });
              onStartRestTimer(
                sessionId,
                item.exerciseId,
                nameForExercise(item, language),
                defaultRestSeconds
              );
              await refresh();
            })
          }
        >
          {t.addSet}
        </button>
      </div>
      {defaultRestSeconds > 0 ? (
        <button
          className="secondary rest-start-button"
          type="button"
          onClick={() =>
            onStartRestTimer(sessionId, item.exerciseId, nameForExercise(item, language), defaultRestSeconds)
          }
        >
          <Timer size={17} aria-hidden="true" />
          {t.startRest} · {formatCountdown(defaultRestSeconds)}
        </button>
      ) : null}
      <details className="training-disclosure">
        <summary>{t.completedSets}<span>{item.sets.length}</span><ChevronDown size={18} aria-hidden="true" /></summary>
      <div className="set-list">
        {item.sets.map((set) => (
            <div className="set-row" key={set.id}>
              <span>#{set.position}</span>
              {showWeight ? <strong>{set.weightKg ?? "-"} kg</strong> : null}
              {showReps ? <strong>{set.reps ?? "-"} reps</strong> : null}
              {showDuration ? <strong>{set.durationSeconds ? Number((set.durationSeconds / 60).toFixed(1)) : "-"} min</strong> : null}
              {showDistance ? <strong>{set.distanceMeters ?? "-"} m</strong> : null}
              <span>RPE {set.rpe ?? "-"}</span>
              {showRir ? <span>RIR {set.rir ?? "-"}</span> : null}
            </div>
          ))}
        </div>
        {!item.sets.length ? <p className="muted">{t.noSets}</p> : null}
      </details>
      <details className="training-disclosure">
        <summary>{t.trainingReference}<ChevronDown size={18} aria-hidden="true" /></summary>
      <PreviousPerformance t={t} previous={previous} />
      <NoteEditor
        t={t}
        label={t.exerciseNotes}
        value={exerciseNotes}
        onChange={setExerciseNotes}
        onSave={() =>
          run(async () => {
            await api(`/api/session-exercises/${item.id}`, {
              method: "PATCH",
              body: JSON.stringify({ notes: exerciseNotes })
            });
            await refresh();
          })
        }
      />
      </details>
    </article>
  );
}

function SessionDetail({
  t,
  language,
  session,
  title,
  onBack,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  session: TrainingSession;
  title: string;
  onBack?: () => void;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [sessionNotes, setSessionNotes] = React.useState(session.notes ?? "");

  React.useEffect(() => {
    setSessionNotes(session.notes ?? "");
  }, [session.id, session.notes]);

  return (
    <section className="stack">
      <article className="panel compact">
        {onBack ? (
          <button className="secondary inline-action" type="button" onClick={onBack}>
            <ArrowLeft size={17} /> {t.backToHistory}
          </button>
        ) : null}
        <div className="session-detail-head">
          <div>
            <h2 className="detail-title">
              {session.endedAt ? <CheckCircle2 size={18} aria-hidden="true" /> : null}
              {title}
            </h2>
            <span className="muted">{session.trainedOn}</span>
          </div>
          <span className={session.endedAt ? "badge ok" : "badge low"}>
            {session.endedAt ? t.ended : t.inProgress}
          </span>
        </div>
        <div className="metric-strip">
          <span>{t.fatigue}: {session.fatigueScore ?? "-"}</span>
          <span>{t.pain}: {session.painScore ?? "-"}</span>
          <span>{session.endedAt ? completedExercises(session.exercises).length : session.exercises.length} {t.exerciseCount}</span>
        </div>
        <NoteEditor
          t={t}
          label={t.sessionNotes}
          value={sessionNotes}
          onChange={setSessionNotes}
          onSave={() =>
            run(async () => {
              await api(`/api/sessions/${session.id}`, {
                method: "PATCH",
                body: JSON.stringify({ notes: sessionNotes })
              });
              await refresh();
            })
          }
        />
        {session.recoveryCheckin ? (
          <RecoverySummary t={t} language={language} checkin={session.recoveryCheckin} />
        ) : null}
        {session.endedAt ? (
          <OverallEvaluationCard t={t} language={language} evaluation={session.overallEvaluation} />
        ) : null}
      </article>
      {session.exercises.map((exercise) => (
        <CompletedExerciseCard
          key={exercise.id}
          t={t}
          language={language}
          exercise={exercise}
          showEvaluation={Boolean(session.endedAt)}
          refresh={refresh}
          run={run}
        />
      ))}
    </section>
  );
}

function OverallEvaluationCard({
  t,
  language,
  evaluation
}: {
  t: Record<string, string>;
  language: Language;
  evaluation: SessionOverallEvaluation | null;
}) {
  if (!evaluation) {
    return (
      <section className="overall-evaluation pending">
        <div className="overall-evaluation-head">
          <div>
            <ClipboardCheck size={18} aria-hidden="true" />
            <strong>{t.overallEvaluation}</strong>
          </div>
          <span className="badge low">{t.overallPending}</span>
        </div>
      </section>
    );
  }

  const evaluatedAt = formatEvaluationTime(evaluation.evaluatedAt, language);
  return (
    <section className="overall-evaluation">
      <div className="overall-evaluation-head">
        <div>
          <ClipboardCheck size={18} aria-hidden="true" />
          <strong>{t.overallEvaluation}</strong>
        </div>
        <span className={`badge ${evaluation.status === "final" ? "ok" : "low"}`}>
          {evaluation.status === "final" ? t.evaluationFinal : t.evaluationProvisional}
        </span>
      </div>
      <div className="overall-evaluation-summary">
        <h3>{evaluation.headline}</h3>
        <p>{evaluation.summary}</p>
      </div>
      <div className="overall-evaluation-sections">
        {evaluation.progress.length > 0 ? (
          <EvaluationList tone="progress" icon={<TrendingUp size={17} />} title={t.overallProgress} items={evaluation.progress} />
        ) : null}
        {evaluation.attention.length > 0 ? (
          <EvaluationList tone="attention" icon={<TriangleAlert size={17} />} title={t.overallAttention} items={evaluation.attention} />
        ) : null}
        <div className="overall-evaluation-section capacity">
          <div>
            <Gauge size={17} aria-hidden="true" />
            <strong>{t.overallCapacity}</strong>
          </div>
          <p>{evaluation.capacitySummary}</p>
        </div>
        {evaluation.nextFocus.length > 0 ? (
          <EvaluationList tone="focus" icon={<Target size={17} />} title={t.overallNextFocus} items={evaluation.nextFocus} />
        ) : null}
      </div>
      <span className="overall-evaluation-time">{t.evaluatedAt} · {evaluatedAt}</span>
    </section>
  );
}

function EvaluationList({
  icon,
  title,
  items,
  tone
}: {
  icon: React.ReactNode;
  title: string;
  items: string[];
  tone: "progress" | "attention" | "focus";
}) {
  return (
    <div className={`overall-evaluation-section ${tone}`}>
      <div>
        {icon}
        <strong>{title}</strong>
      </div>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}

function CompletedExerciseCard({
  t,
  language,
  exercise,
  showEvaluation,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  exercise: TrainingSession["exercises"][number];
  showEvaluation: boolean;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [notes, setNotes] = React.useState(exercise.notes ?? "");

  React.useEffect(() => {
    setNotes(exercise.notes ?? "");
  }, [exercise.id, exercise.notes]);

  return (
    <article className="panel">
      <h2>{nameForExercise(exercise, language)}</h2>
      {exercise.sets.length === 0 ? <p className="muted">{t.noSets}</p> : null}
      <ReadOnlySetList t={t} item={exercise} />
      <NoteEditor
        t={t}
        label={t.exerciseNotes}
        value={notes}
        onChange={setNotes}
        onSave={() =>
          run(async () => {
            await api(`/api/session-exercises/${exercise.id}`, {
              method: "PATCH",
              body: JSON.stringify({ notes })
            });
            await refresh();
          })
        }
      />
      {showEvaluation ? (
        <div className="exercise-evaluation">
          <div className="exercise-evaluation-title">
            <MessageSquareText size={17} aria-hidden="true" />
            <strong>{t.nextExerciseAdvice}</strong>
          </div>
          <p>{exercise.coachEvaluation ?? t.evaluationPending}</p>
        </div>
      ) : null}
    </article>
  );
}

function NoteEditor({
  t,
  label,
  value,
  onChange,
  onSave
}: {
  t: Record<string, string>;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<void>;
}) {
  return (
    <div className="note-editor">
      <label>
        <span>{label}</span>
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t.notePlaceholder}
          rows={3}
        />
      </label>
      <button className="secondary" type="button" onClick={() => void onSave()}>
        <Save size={16} aria-hidden="true" /> {t.saveNote}
      </button>
    </div>
  );
}

function ReadOnlySetList({ t, item }: { t: Record<string, string>; item: TrainingSession["exercises"][number] }) {
  const isTimed = item.trackingType === "timed";
  const isCarry = item.trackingType === "carry";
  const isDistance = item.trackingType === "distance";
  const isHold = item.trackingType === "hold";
  const showWeight = item.trackingType === "strength" || isCarry || isHold;
  const showReps = item.trackingType === "strength";
  const showDuration = isTimed || isCarry || isDistance || isHold;
  const showDistance = isCarry || isDistance;
  const showRir = item.trackingType === "strength";

  return (
    <div className="set-list">
      {item.sets.map((set) => (
        <div className="set-row" key={set.id}>
          <span>#{set.position}</span>
          {showWeight ? <strong>{set.weightKg ?? "-"} kg</strong> : null}
          {showReps ? <strong>{set.reps ?? "-"} reps</strong> : null}
          {showDuration ? <strong>{set.durationSeconds ? Number((set.durationSeconds / 60).toFixed(1)) : "-"} min</strong> : null}
          {showDistance ? <strong>{set.distanceMeters ?? "-"} m</strong> : null}
          <span>RPE {set.rpe ?? "-"}</span>
          {showRir ? <span>RIR {set.rir ?? "-"}</span> : null}
        </div>
      ))}
    </div>
  );
}

function sorenessLabel(t: Record<string, string>, level: SorenessLevel): string {
  return t[`soreness${level}`];
}

function RecoverySummary({
  t,
  language,
  checkin
}: {
  t: Record<string, string>;
  language: Language;
  checkin: NonNullable<TrainingSession["recoveryCheckin"]>;
}) {
  return (
    <div className="recovery-summary">
      <div className="recovery-summary-head">
        <strong>{t.recoveryTitle}</strong>
        <span>{checkin.checkinOn}</span>
      </div>
      <div className="recovery-summary-items">
        {checkin.soreness.map((item) => (
          <span className={`soreness-tag level-${item.level}`} key={item.muscleGroupId}>
            {language === "ja-JP" ? item.muscleNameJa : item.muscleNameZh} · {sorenessLabel(t, item.level)}
          </span>
        ))}
      </div>
      {checkin.notes ? <p>{checkin.notes}</p> : null}
    </div>
  );
}

function RecoveryView({
  t,
  language,
  recoveryWindow,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  recoveryWindow: RecoveryWindow;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [levels, setLevels] = React.useState<Record<number, SorenessLevel>>(() =>
    Object.fromEntries(
      recoveryWindow.muscles
        .filter((muscle) => muscle.level !== null)
        .map((muscle) => [muscle.muscleGroupId, muscle.level as SorenessLevel])
    )
  );
  const [notes, setNotes] = React.useState(recoveryWindow.notes ?? "");

  const allRated =
    recoveryWindow.muscles.length > 0 &&
    recoveryWindow.muscles.every((muscle) => levels[muscle.muscleGroupId] !== undefined);
  const exerciseNames =
    language === "ja-JP"
      ? recoveryWindow.exerciseNamesJa.map((name, index) => name ?? recoveryWindow.exerciseNamesZh[index])
      : recoveryWindow.exerciseNamesZh;

  return (
    <section className="stack">
      <article className="panel recovery-panel">
        <div className="recovery-page-head">
          <div>
            <h2>
              <HeartPulse size={19} aria-hidden="true" /> {t.recoveryTitle}
            </h2>
            <span>{recoveryWindow.trainedOn} → {recoveryWindow.checkinOn}</span>
          </div>
          <span className={recoveryWindow.completed ? "badge ok" : "badge low"}>
            {recoveryWindow.completed ? t.recoveryRecorded : t.recoveryPending}
          </span>
        </div>
        <div className="recovery-exercises">
          <strong>{t.recoveryExercises}</strong>
          <span>{exerciseNames.join("、")}</span>
        </div>
      </article>

      {recoveryWindow.muscles.map((muscle) => (
        <fieldset className="panel soreness-field" key={muscle.muscleGroupId}>
          <legend>{language === "ja-JP" ? muscle.muscleNameJa : muscle.muscleNameZh}</legend>
          <div className="soreness-options">
            {SORENESS_LEVELS.map((level) => (
              <button
                className={levels[muscle.muscleGroupId] === level ? `selected level-${level}` : ""}
                type="button"
                aria-pressed={levels[muscle.muscleGroupId] === level}
                key={level}
                onClick={() => setLevels((current) => ({ ...current, [muscle.muscleGroupId]: level }))}
              >
                {sorenessLabel(t, level)}
              </button>
            ))}
          </div>
        </fieldset>
      ))}

      <article className="panel">
        <label>
          <span>{t.recoveryNotes}</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t.notePlaceholder}
            rows={3}
          />
        </label>
        <button
          className="primary"
          type="button"
          disabled={!allRated}
          onClick={() =>
            void run(async () => {
              await api(`/api/recovery/${recoveryWindow.sessionId}`, {
                method: "PUT",
                body: JSON.stringify({
                  entries: recoveryWindow.muscles.map((muscle) => ({
                    muscleGroupId: muscle.muscleGroupId,
                    level: levels[muscle.muscleGroupId]
                  })),
                  notes
                })
              });
              await refresh();
            })
          }
        >
          <Save size={17} aria-hidden="true" /> {t.saveRecovery}
        </button>
      </article>
    </section>
  );
}

function InsightsView({
  t,
  language,
  weeklyVolume,
  recommendation,
  recoveryWindow,
  sessions,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  weeklyVolume: WeeklyVolume;
  recommendation: NextSessionRecommendation | null;
  recoveryWindow: RecoveryWindow | null;
  sessions: TrainingSession[];
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const evaluatedSession = sessions.find((session) => session.endedAt && session.overallEvaluation) ?? null;
  return (
    <section className="stack insights-page">
      <header className="page-heading"><span>{t.insightsNav}</span><h1>{t.insightsTitle}</h1></header>
      <RecommendationCard t={t} recommendation={recommendation} />
      <VolumeView t={t} language={language} weeklyVolume={weeklyVolume} />
      {evaluatedSession ? (
        <details className="insight-details" open>
          <summary><ClipboardCheck size={18} /> {t.overallEvaluation}<ChevronDown size={18} /></summary>
          <div className="panel"><OverallEvaluationCard t={t} language={language} evaluation={evaluatedSession.overallEvaluation} /></div>
        </details>
      ) : null}
      {recoveryWindow ? <RecoveryView key={recoveryWindow.sessionId} t={t} language={language} recoveryWindow={recoveryWindow} refresh={refresh} run={run} /> : null}
    </section>
  );
}

function ProfileView({
  t,
  language,
  setLanguage,
  bodyMetrics,
  installAvailable,
  onInstall,
  serviceWorkerUpdate,
  onUpdate,
  onLogout,
  refresh,
  run
}: {
  t: Record<string, string>;
  language: Language;
  setLanguage: (language: Language) => void;
  bodyMetrics: BodyMetric[];
  installAvailable: boolean;
  onInstall: () => Promise<void>;
  serviceWorkerUpdate: ServiceWorkerRegistration | null;
  onUpdate: () => void;
  onLogout: () => Promise<void>;
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  return (
    <section className="stack profile-page">
      <header className="page-heading"><span>{t.profileNav}</span><h1>{t.profileTitle}</h1></header>
      <BodyView t={t} bodyMetrics={bodyMetrics} refresh={refresh} run={run} />
      <section className="profile-group">
        <h2>{t.appSection}</h2>
        <div className="profile-list">
          <button type="button" onClick={() => setLanguage(language === "zh-CN" ? "ja-JP" : "zh-CN")}><Globe2 size={19} /><span>{t.language}</span><strong>{language === "zh-CN" ? "中文" : "日本語"}</strong><ChevronRight size={18} /></button>
          <button type="button" disabled={!installAvailable} onClick={() => void onInstall()}><Download size={19} /><span>{t.installApp}</span><ChevronRight size={18} /></button>
          <a href="/api/android/download"><Download size={19} /><span>{t.downloadAndroid}</span><ChevronRight size={18} /></a>
          <div><Activity size={19} /><span>{t.appVersion}</span><strong>{APP_VERSION}</strong></div>
          {serviceWorkerUpdate ? <button type="button" onClick={onUpdate}><RotateCcw size={19} /><span>{t.updateReady}</span><strong>{t.updateNow}</strong></button> : null}
        </div>
        {!installAvailable ? <p className="muted">{t.installUnavailable}</p> : null}
      </section>
      <section className="profile-group">
        <h2>{t.account}</h2>
        <div className="profile-list">
          <div><CheckCircle2 size={19} /><span>{t.signedIn}</span></div>
          <button className="logout-row" type="button" onClick={() => void onLogout()}><LogOut size={19} /><span>{t.logout}</span><ChevronRight size={18} /></button>
        </div>
      </section>
    </section>
  );
}

function VolumeView({ t, language, weeklyVolume }: { t: Record<string, string>; language: Language; weeklyVolume: WeeklyVolume }) {
  return (
    <section className="stack">
      <div className="section-title">
        <h2>{t.week}</h2>
        <span>{weeklyVolume.weekStart} - {weeklyVolume.weekEnd}</span>
      </div>
      <div className="panel volume-card">
      {weeklyVolume.muscles.map((muscle) => {
        const max = Math.max(muscle.targetMax, muscle.effectiveSets, 1);
        return (
          <div className="volume-row" key={muscle.slug}>
            <div className="volume-head">
              <strong>{language === "ja-JP" ? muscle.nameJa : muscle.nameZh}</strong>
              <span className={`badge ${muscle.status}`}>{t[muscle.status]}</span>
            </div>
            <div className="bar">
              <span style={{ width: `${Math.min(100, (muscle.effectiveSets / max) * 100)}%` }} />
            </div>
            <div className="volume-foot">
              <span>{muscle.effectiveSets} sets</span>
              <span>{muscle.targetMin}-{muscle.targetMax}</span>
            </div>
          </div>
        );
      })}
      </div>
    </section>
  );
}

function HistoryView({
  t,
  language,
  sessions,
  onOpenSession
}: {
  t: Record<string, string>;
  language: Language;
  sessions: TrainingSession[];
  onOpenSession: (sessionId: number) => void;
}) {
  if (sessions.length === 0) return <p className="muted">{t.noHistory}</p>;
  return (
    <section className="stack">
      <header className="page-heading"><span>{t.history}</span><h1>{t.history}</h1></header>
      {sessions.map((session) => (
        <button className="panel history-card" type="button" key={session.id} onClick={() => onOpenSession(session.id)}>
          <div className="history-card-head">
            <div>
              <h2>{session.trainedOn}</h2>
              <span className="muted">{completedExercises(session.exercises).length} {t.exerciseCount}</span>
            </div>
            <ChevronRight size={19} aria-hidden="true" />
          </div>
          <div className="metric-strip">
            <span>{session.endedAt ? `${sessionDurationMinutes(session)} min` : t.inProgress}</span>
            <span>{completedExercises(session.exercises).length} {t.exerciseCount}</span>
            <span>{completedSetCount(session.exercises)} {t.setCount}</span>
            <span>{session.endedAt ? t.ended : t.inProgress}</span>
          </div>
          <div className="history-exercise-list">
            {completedExercises(session.exercises).slice(0, 4).map((exercise) => (
              <div className="history-exercise" key={exercise.id}>
                <strong>{nameForExercise(exercise, language)}</strong>
                <span>{completedSetCount([exercise])} {t.setCount}</span>
              </div>
            ))}
          </div>
          <span className="history-action">
            {t.viewDetail}
          </span>
        </button>
        ))}
      </section>
    );
  }

function BodyView({
  t,
  bodyMetrics,
  refresh,
  run
}: {
  t: Record<string, string>;
  bodyMetrics: BodyMetric[];
  refresh: () => Promise<void>;
  run: (action: () => Promise<void>) => Promise<void>;
}) {
  const [bodyWeightKg, setBodyWeightKg] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const orderedMetrics = [...bodyMetrics].sort((left, right) => left.measuredOn.localeCompare(right.measuredOn));
  const latestMetric = orderedMetrics.at(-1);
  const previousMetric = orderedMetrics.at(-2);
  const change = latestMetric && previousMetric ? decimalDifference(latestMetric.bodyWeightKg, previousMetric.bodyWeightKg) : null;
  return (
    <section className="stack">
      {latestMetric ? (
        <div className="panel body-summary">
          <div><span>{t.currentWeight}</span><strong>{latestMetric.bodyWeightKg}<small> kg</small></strong></div>
          <div><span>{t.weightChange}</span><strong className={change !== null && change > 0 ? "up" : "down"}>{change === null ? "-" : `${change > 0 ? "+" : ""}${change} kg`}</strong></div>
          <BodyTrend metrics={orderedMetrics} />
        </div>
      ) : null}
      <div className="panel">
        <NumberInput label={t.bodyWeight} value={bodyWeightKg} onChange={setBodyWeightKg} />
        <label>
          <span>{t.notes}</span>
          <input value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        <button
          className="primary"
          type="button"
          onClick={() =>
            void run(async () => {
              await api("/api/body-metrics", {
                method: "POST",
                body: JSON.stringify({ measuredOn: getLocalDateString(), bodyWeightKg, notes })
              });
              setBodyWeightKg("");
              setNotes("");
              await refresh();
            })
          }
        >
          {t.save}
        </button>
      </div>
      {bodyMetrics.length === 0 ? <p className="muted">{t.noMetrics}</p> : null}
      {bodyMetrics.map((metric) => (
        <div className="panel compact metric-row" key={metric.id}>
          <strong>{metric.measuredOn}</strong>
          <span>{metric.bodyWeightKg} kg</span>
        </div>
      ))}
    </section>
  );
}

function decimalDifference(current: number, previous: number): number {
  const decimals = Math.max(String(current).split(".")[1]?.length ?? 0, String(previous).split(".")[1]?.length ?? 0);
  const scale = 10 ** decimals;
  return (Math.round(current * scale) - Math.round(previous * scale)) / scale;
}

function BodyTrend({ metrics }: { metrics: BodyMetric[] }) {
  if (metrics.length < 2) return null;
  const values = metrics.map((metric) => metric.bodyWeightKg);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const range = maximum - minimum || 1;
  const points = metrics.map((metric, index) => {
    const x = metrics.length === 1 ? 160 : (index / (metrics.length - 1)) * 300 + 10;
    const y = 85 - ((metric.bodyWeightKg - minimum) / range) * 70;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg className="body-trend" viewBox="0 0 320 100" role="img" aria-label="Body weight trend">
      <polyline points={points} fill="none" vectorEffect="non-scaling-stroke" />
      {points.split(" ").map((point, index) => {
        const [cx, cy] = point.split(",");
        return <circle key={metrics[index].id} cx={cx} cy={cy} r="3" />;
      })}
    </svg>
  );
}

function Slider({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label>
      <span>{label}: {value}</span>
      <input type="range" min={0} max={10} step={1} value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function NumberInput({
  label,
  hint,
  value,
  onChange
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="input-label">
        <span>{label}</span>
        {hint ? <small>{hint}</small> : null}
      </span>
      <input inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function MetricSlider({
  t, label, value, onChange, step, fineStep = step, initialMax, maximum, radius, baseline
}: {
  t: Record<string, string>;
  label: string;
  value: string;
  onChange: (value: string) => void;
  step: number;
  fineStep?: number;
  initialMax: number;
  maximum: number;
  radius?: number;
  baseline?: number | null;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [anchor, setAnchor] = React.useState(() => Number(value) || 0);
  React.useEffect(() => { if (baseline != null) setAnchor(baseline); }, [baseline]);
  const id = React.useId();
  const numeric = Number(value) || 0;
  const minimum = expanded || !radius ? 0 : Math.max(0, anchor - radius);
  const max = Math.max(numeric, expanded ? maximum : radius ? Math.max(initialMax, anchor + radius) : initialMax);
  const min = Math.min(minimum, numeric);
  function adjust(direction: -1 | 1) {
    const next = Math.max(0, Math.min(Math.max(maximum, numeric), numeric + direction * step));
    onChange(String(Number(next.toFixed(4))));
  }
  return (
    <div className="metric-slider">
      <div className="metric-slider-header"><label htmlFor={id}>{label}</label><output htmlFor={id}>{value === "" ? t.notSet : Number(numeric.toFixed(2))}</output></div>
      <div className="metric-slider-controls">
        <button type="button" aria-label={`${label} -${step}`} disabled={numeric <= 0 && value !== ""} onClick={() => adjust(-1)}><Minus size={22} aria-hidden="true" /></button>
        <input id={id} aria-label={label} type="range" min={min} max={max} step={fineStep} value={numeric}
          onChange={event => onChange(event.target.value)} />
        <button type="button" aria-label={`${label} +${step}`} disabled={numeric >= Math.max(maximum, numeric)} onClick={() => adjust(1)}><Plus size={22} aria-hidden="true" /></button>
      </div>
      <div className="metric-slider-scale"><span>{min}</span><button type="button" aria-expanded={expanded}
        onClick={() => { setAnchor(numeric); setExpanded(!expanded); }}>{expanded ? t.nearbyRange : t.fullRange}</button><span>{max}</span></div>
    </div>
  );
}

function EffortSlider({
  label,
  hint,
  min,
  max,
  value,
  onChange
}: {
  label: string;
  hint: string;
  min: number;
  max: number;
  value: string;
  onChange: (value: string) => void;
}) {
  const numericValue = Math.min(max, Math.max(min, Number(value)));

  return (
    <label className="effort-slider">
      <span className="input-label">
        <span>{label}</span>
        <output>{numericValue}</output>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={numericValue}
        aria-label={`${label}: ${numericValue}`}
        aria-description={hint}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="effort-slider-scale">
        <span>{min}</span>
        <small>{hint}</small>
        <span>{max}</span>
      </span>
    </label>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
